import Phaser from 'phaser'
import { getNetworkClient, NetworkClient, ConnectionState } from '../NetworkClient'
import { IS_MOBILE } from '../main'

interface LobbySceneData {
  action: 'create' | 'join'
  roomId?: string
}

export class LobbyScene extends Phaser.Scene {
  private network!: NetworkClient
  private statusText!: Phaser.GameObjects.Text
  private playersText!: Phaser.GameObjects.Text
  private roomIdText!: Phaser.GameObjects.Text
  private startButton!: Phaser.GameObjects.Container
  private action: 'create' | 'join' = 'create'
  private roomId: string = ''
  private playerCount: number = 0
  private myPlayerId: string = ''
  private isHost: boolean = false
  private isMobile: boolean = IS_MOBILE

  constructor() {
    super({ key: 'LobbyScene' })
  }

  init(data: LobbySceneData) {
    this.action = data.action || 'create'
    this.roomId = data.roomId || this.generateRoomId()
    this.playerCount = 0
    this.myPlayerId = ''
    this.isHost = data.action === 'create'
  }

  create() {
    const width = this.cameras.main.width
    const height = this.cameras.main.height

    // Background
    const graphics = this.add.graphics()
    graphics.fillStyle(0x0a0a15)
    graphics.fillRect(0, 0, width, height)

    // Grid pattern
    graphics.lineStyle(1, 0x1a1a2e, 0.5)
    for (let x = 0; x < width; x += 40) {
      graphics.lineBetween(x, 0, x, height)
    }
    for (let y = 0; y < height; y += 40) {
      graphics.lineBetween(0, y, width, y)
    }

    // Title
    this.add.text(width / 2, this.isMobile ? 50 : 80, 'MULTIPLAYER LOBBY', {
      fontSize: this.isMobile ? '28px' : '42px',
      fontFamily: 'monospace',
      color: '#4a90d9',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    // Room ID display
    const roomPanelY = this.isMobile ? 130 : 180
    const roomPanelWidth = this.isMobile ? 340 : 400
    const roomPanel = this.add.rectangle(width / 2, roomPanelY, roomPanelWidth, this.isMobile ? 70 : 80, 0x1a1a2e)
    roomPanel.setStrokeStyle(2, 0x4a90d9)

    this.add.text(width / 2, roomPanelY - 25, 'ROOM CODE', {
      fontSize: this.isMobile ? '12px' : '14px',
      fontFamily: 'monospace',
      color: '#666666'
    }).setOrigin(0.5)

    this.roomIdText = this.add.text(width / 2, roomPanelY + 5, this.roomId, {
      fontSize: this.isMobile ? '28px' : '36px',
      fontFamily: 'monospace',
      color: '#00ff88',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    // Copy button
    const copyBtn = this.add.text(width / 2 + (this.isMobile ? 120 : 150), roomPanelY + 5, '[COPY]', {
      fontSize: this.isMobile ? '12px' : '14px',
      fontFamily: 'monospace',
      color: '#4a90d9'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true })

    copyBtn.on('pointerdown', () => {
      navigator.clipboard?.writeText(this.roomId)
      copyBtn.setText('[COPIED!]')
      this.time.delayedCall(1500, () => copyBtn.setText('[COPY]'))
    })

    copyBtn.on('pointerover', () => copyBtn.setColor('#6ab0f9'))
    copyBtn.on('pointerout', () => copyBtn.setColor('#4a90d9'))

    // Players panel (sized for 4 player slots)
    const playersPanelY = this.isMobile ? 280 : 340
    const playersPanelWidth = this.isMobile ? 400 : 500
    const playersPanelHeight = this.isMobile ? 160 : 180
    const playersPanel = this.add.rectangle(width / 2, playersPanelY, playersPanelWidth, playersPanelHeight, 0x1a1a2e)
    playersPanel.setStrokeStyle(2, 0x2a2a3e)

    this.add.text(width / 2, playersPanelY - (this.isMobile ? 70 : 80), 'PLAYERS (2-4)', {
      fontSize: this.isMobile ? '14px' : '16px',
      fontFamily: 'monospace',
      color: '#666666'
    }).setOrigin(0.5)

    this.playersText = this.add.text(width / 2, playersPanelY, 'Waiting for players...', {
      fontSize: this.isMobile ? '16px' : '18px',
      fontFamily: 'monospace',
      color: '#aaaaaa',
      align: 'center'
    }).setOrigin(0.5)

    // Status text
    this.statusText = this.add.text(width / 2, this.isMobile ? 410 : 490, 'Connecting...', {
      fontSize: this.isMobile ? '14px' : '16px',
      fontFamily: 'monospace',
      color: '#ffcc00'
    }).setOrigin(0.5)

    // Start button (for host)
    this.createStartButton()

    // Back button
    this.createBackButton()

    // Instructions
    const instructionText = this.isHost
      ? (this.isMobile ? 'Share code! Start with 2-4 players' : 'Share the room code! You can start with 2-4 players.')
      : 'Waiting for the host to start...'
    this.add.text(width / 2, height - (this.isMobile ? 40 : 60), instructionText, {
      fontSize: this.isMobile ? '12px' : '14px',
      fontFamily: 'monospace',
      color: '#666666'
    }).setOrigin(0.5)

    // Connect to server
    this.connectToServer()
  }

  private generateRoomId(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    let result = ''
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  private createStartButton() {
    const width = this.cameras.main.width

    this.startButton = this.add.container(width / 2, this.isMobile ? 480 : 560)
    this.startButton.setVisible(false)

    const btnWidth = this.isMobile ? 220 : 200
    const btnHeight = this.isMobile ? 60 : 55
    const bg = this.add.rectangle(0, 0, btnWidth, btnHeight, 0x2a5a2a)
    bg.setStrokeStyle(2, 0x3a8a3a)
    bg.setInteractive({ useHandCursor: true })

    const text = this.add.text(0, 0, 'START GAME', {
      fontSize: this.isMobile ? '18px' : '20px',
      fontFamily: 'monospace',
      color: '#00ff88',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    this.startButton.add([bg, text])

    bg.on('pointerover', () => bg.setFillStyle(0x3a7a3a))
    bg.on('pointerout', () => bg.setFillStyle(0x2a5a2a))
    bg.on('pointerdown', () => {
      if (this.playerCount >= 2) {
        this.network.ready()
      }
    })
  }

  private createBackButton() {
    const backBtn = this.add.text(this.isMobile ? 30 : 50, this.isMobile ? 20 : 50, '< BACK', {
      fontSize: this.isMobile ? '16px' : '18px',
      fontFamily: 'monospace',
      color: '#888888'
    }).setInteractive({ useHandCursor: true })

    backBtn.on('pointerover', () => backBtn.setColor('#ffffff'))
    backBtn.on('pointerout', () => backBtn.setColor('#888888'))
    backBtn.on('pointerdown', () => {
      this.network.disconnect()
      this.scene.start('MenuScene')
    })
  }

  private async connectToServer() {
    // Get the server URL - use the deployed worker URL
    const serverUrl = this.getServerUrl()

    try {
      this.network = getNetworkClient(serverUrl)

      this.network.on('onStateChange', (state: ConnectionState) => {
        this.handleConnectionState(state)
      })

      this.network.on('onGameState', (gameState) => {
        const players = Object.values(gameState.players)
        this.playerCount = players.length

        if (players.length === 1) {
          this.myPlayerId = players[0].id
        }

        this.updatePlayersDisplay(players.map(p => p.name))

        // If game is starting, transition to game scene
        if (gameState.phase === 'building' || gameState.phase === 'wave') {
          this.scene.start('GameScene', {
            mode: 'multi',
            roomId: this.roomId,
            playerId: this.myPlayerId
          })
        }
      })

      this.network.on('onPlayerJoined', (player) => {
        this.playerCount++
        if (this.playerCount === 1) {
          this.myPlayerId = player.id
        }

        // Show start button when 2 players are connected (host only)
        if (this.isHost && this.playerCount >= 2) {
          this.startButton.setVisible(true)
          this.statusText.setText('Ready to start!')
          this.statusText.setColor('#00ff88')
        }
      })

      this.network.on('onWaveStart', () => {
        // Game is starting
        this.scene.start('GameScene', {
          mode: 'multi',
          roomId: this.roomId,
          playerId: this.myPlayerId
        })
      })

      this.network.on('onError', (message) => {
        this.statusText.setText(`Error: ${message}`)
        this.statusText.setColor('#ff4444')
      })

      const connected = await this.network.connect(this.roomId)

      if (connected) {
        // Join the game with a player name
        const playerName = `Player ${Math.floor(Math.random() * 1000)}`
        this.network.join(playerName)
      }
    } catch (e) {
      this.statusText.setText('Failed to connect to server')
      this.statusText.setColor('#ff4444')
    }
  }

  private getServerUrl(): string {
    // In production, use the deployed worker URL
    // In development, use localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'ws://localhost:8787'
    }
    // Use the deployed worker URL
    return 'wss://line-tower-wars.silent-mode-ee83.workers.dev'
  }

  private handleConnectionState(state: ConnectionState) {
    switch (state) {
      case 'connecting':
        this.statusText.setText('Connecting to server...')
        this.statusText.setColor('#ffcc00')
        break
      case 'connected':
        this.statusText.setText('Connected! Waiting for players...')
        this.statusText.setColor('#00ff88')
        break
      case 'disconnected':
        this.statusText.setText('Disconnected from server')
        this.statusText.setColor('#ff4444')
        break
      case 'error':
        this.statusText.setText('Connection error')
        this.statusText.setColor('#ff4444')
        break
    }
  }

  private updatePlayersDisplay(playerNames: string[]) {
    const slots = []
    for (let i = 0; i < 4; i++) {
      if (i < playerNames.length) {
        const isYou = i === 0 && this.isHost || i === playerNames.length - 1 && !this.isHost
        const isHostPlayer = i === 0
        let label = playerNames[i]
        if (isHostPlayer) label += ' (Host)'
        if (isYou) label += ' <- You'
        slots.push(label)
      } else {
        slots.push('[ Empty Slot ]')
      }
    }
    this.playersText.setText(slots.join('\n'))
  }
}
